import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiEye, FiTrash2, FiX, FiDownload, FiFile, FiExternalLink } from "react-icons/fi";

const AllRaisedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Change this to your actual backend URL (without /api/admin)
  const BACKEND_BASE = "https://api.elthiumhealth.com";
  const API_BASE = `${BACKEND_BASE}/api/admin`;

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/allraisedtickets`);
      if (response.data?.success) {
        setTickets(response.data.tickets || []);
      } else {
        setTickets([]);
        setErrorMessage(response.data?.message || "Failed to load tickets");
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setErrorMessage("Failed to load tickets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleDelete = async (ticketId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this ticket?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/deleteraisedtickets/${ticketId}`);
      setTickets((prev) => prev.filter((t) => t._id !== ticketId));
      setErrorMessage("Ticket deleted successfully.");
      setTimeout(() => setErrorMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting ticket:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to delete ticket. Please try again."
      );
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      Open: "bg-green-100 text-green-800",
      "In Progress": "bg-yellow-100 text-yellow-800",
      Closed: "bg-gray-100 text-gray-800",
      Resolved: "bg-blue-100 text-blue-800",
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  // Construct full attachment URL
  const getAttachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return null;
    if (attachmentPath.startsWith("http")) return attachmentPath;
    const cleanPath = attachmentPath.startsWith("/") ? attachmentPath.slice(1) : attachmentPath;
    return `${BACKEND_BASE}/${cleanPath}`;
  };

  // Force download file
  const downloadFile = (url, filename) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename || 'attachment';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Download failed:', error);
        // Fallback: open in new tab
        window.open(url, '_blank');
      });
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-lg font-medium text-gray-600">
        Loading tickets...
      </div>
    );
  }

  if (tickets.length === 0 && !errorMessage) {
    return (
      <div className="text-center py-10 text-gray-500">
        No support tickets found.
      </div>
    );
  }

  return (
    <div className="p-6">
      {errorMessage && (
        <div className="bg-yellow-100 text-yellow-800 text-center py-2 rounded mb-4">
          {errorMessage}
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">S.No</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Reason</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Raised By</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tickets.map((ticket, index) => {
              const {
                _id,
                reason,
                description,
                status,
                createdAt,
                staffId,
              } = ticket;

              const staffName = staffId?.name || "Unknown";
              const shortDescription = description?.length > 60
                ? `${description.slice(0, 60)}...`
                : description || "";

              return (
                <tr key={_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-700">{index + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{reason}</td>
                  <td className="px-4 py-3 text-sm text-gray-600" title={description}>
                    {shortDescription}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(status)}`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{staffName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(createdAt)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center space-x-2">
                      <button onClick={() => handleView(ticket)} className="text-blue-600 hover:text-blue-800 p-1" title="View Details">
                        <FiEye size={18} />
                      </button>
                      <button onClick={() => handleDelete(_id)} className="text-red-600 hover:text-red-800 p-1" title="Delete Ticket">
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* View Ticket Modal with Download + Open in New Tab */}
      {showModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">Ticket Details</h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <FiX size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ticket ID</label>
                  <p className="mt-1 text-gray-900 font-mono text-sm">{selectedTicket._id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedTicket.status)}`}>
                      {selectedTicket.status}
                    </span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Reason</label>
                <p className="mt-1 text-gray-900">{selectedTicket.reason}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg text-gray-800 whitespace-pre-wrap">
                  {selectedTicket.description}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-semibold text-gray-800 mb-2">Raised By (Staff)</h4>
                <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                  <p><span className="font-medium">Name:</span> {selectedTicket.staffId?.name || "N/A"}</p>
                  <p><span className="font-medium">Email:</span> {selectedTicket.staffId?.email || "N/A"}</p>
                  <p><span className="font-medium">Contact:</span> {selectedTicket.staffId?.contact_number || "N/A"}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Created At</label>
                <p className="mt-1 text-gray-900">{formatDate(selectedTicket.createdAt)}</p>
              </div>

              {selectedTicket.attachment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Attachment</label>
                  <div className="mt-2">
                    {selectedTicket.attachment.match(/\.(jpeg|jpg|png|gif|webp|bmp)$/i) ? (
                      <div>
                        <img
                          src={getAttachmentUrl(selectedTicket.attachment)}
                          alt="Attachment"
                          className="max-w-full max-h-80 rounded-lg border shadow-sm"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found";
                          }}
                        />
                        <div className="flex gap-3 mt-3">
                          <button
                            onClick={() => downloadFile(getAttachmentUrl(selectedTicket.attachment), `ticket_attachment_${selectedTicket._id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            <FiDownload size={14} /> Download
                          </button>
                          <a
                            href={getAttachmentUrl(selectedTicket.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                          >
                            <FiExternalLink size={14} /> Open in New Tab
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                        <FiFile size={24} className="text-gray-500" />
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => downloadFile(getAttachmentUrl(selectedTicket.attachment), `ticket_attachment_${selectedTicket._id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            <FiDownload size={14} /> Download
                          </button>
                          <a
                            href={getAttachmentUrl(selectedTicket.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                          >
                            <FiExternalLink size={14} /> Open in New Tab
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <button onClick={closeModal} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllRaisedTickets;